import { environment } from "./env";
import { getUserIdToken } from "$lib/stores/user";

/**
 * The error `makeApiRequest` throws on a non-OK response: still an `Error` with the same message,
 * plus the HTTP `status` so a caller can tell e.g. a 404 from a 500. A network failure (fetch
 * rejecting) is not an `ApiError` and has no `status`.
 */
export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

// Reusable functions
export const makeApiRequest = async (path: string, body: object, method = "PUT") => {
  const endpoint = (await environment).apiRoots.management;
  const userToken = await getUserIdToken();
  let headers: Record<string, string> = {};

  // Only set content-type for methods that have a body
  if (method !== "GET" && method !== "HEAD" && method !== "DELETE") {
    headers["content-type"] = "application/json";
  }
  if (!!userToken) {
    headers["Authorization"] = `Bearer ${userToken}`;
  }

  // Build fetch options - don't include body for GET/HEAD/DELETE
  const fetchOptions: RequestInit = {
    method: method,
    headers: headers,
  };

  if (method !== "GET" && method !== "HEAD" && method !== "DELETE") {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(`${endpoint}${path}`, fetchOptions);
  if (!response.ok) {
    if (response.status === 429) {
      const retryAfter = response.headers.get("Retry-After");
      throw new ApiError(
        `Too many requests. Retry after ${retryAfter ? retryAfter + " seconds" : "a while"}.`,
        response.status,
      );
    }

    let data;
    try {
      // Try to get error detail
      data = await response.json();
    } catch (e) {}

    if (data?.detail) {
      throw new ApiError(data.detail, response.status);
    }

    throw new ApiError("Request failed", response.status);
  }
  if (response.status === 204) {
    return;
  }
  return response?.json();
};

export const downloadExportedData = async (orgId: string) => {
  const endpoint = (await environment).apiRoots.management;
  const userToken = await getUserIdToken();
  let headers: Record<string, string> = {
    "content-type": "application/zip",
  };
  if (!!userToken) {
    headers["Authorization"] = `Bearer ${userToken}`;
  }
  const response = await fetch(`${endpoint}/orgs/${orgId}/download-exported-data`, {
    method: "GET",
    headers: headers,
  });
  return response;
};
